import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import {
  TypedUseSelectorHook,
  useSelector as useReduxSelector,
} from 'react-redux';
import { AppState, useAppDispatch } from 'stores/type';
import { showDialog, closeDialog } from 'stores/dialogSlice';
import { localeIds } from 'locals';
import { Application } from 'models/Application/type';
import { ApplicationDomain } from 'models/Application';
import { AvailableApplication } from 'models/Device/type';
import { setErrorMessageLocaleId, setExceptionError } from 'stores/errorSlice';
import { DefaultTemplate } from 'views/templates/DefaultTemplate';
import { Splash } from 'views/components/molecules/Splash';
import { AppDetailPageLayout } from 'views/layouts/AppDetailPageLayout';
import { ROUTER_PATH } from 'views/constants';
import { getRequestPath } from 'utils/string';
import { useDevice } from 'views/hooks/useDevice';

interface AppDetailPageParams {
  packageName: string;
  revision: string;
  userName: string;
  groupName: string;
}

export const AppDetailPage: React.FC = () => {
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const useSelector: TypedUseSelectorHook<AppState> = useReduxSelector;
  const device = useSelector((state) => state.deviceState.device);
  const [application, setApplication] = useState<Application | undefined>(
    undefined
  );
  const [installSessionToken, setInstallSessionToken] = useState<
    string | undefined
  >(undefined);
  const [otherApplications, setOtherApplications] = useState<
    AvailableApplication[]
  >([]);
  const [nextMaxRevision, setNextMaxRevision] = useState<number | undefined>(
    undefined
  );
  const [startInstall, setStartInstall] = useState(false);
  const [startOverwriteInstall, setStartOverwriteInstall] = useState(false);
  const {
    userName,
    groupName,
    packageName,
    revision,
  } = useParams<AppDetailPageParams>();
  const ownerType = userName ? 'users' : 'organizations';
  const ownerName = userName ? userName : groupName;
  const applicationDomain = new ApplicationDomain(
    ownerType,
    ownerName,
    packageName,
    revision,
    device
  );
  const { reInstallConfigurationProfile } = useDevice();

  useEffect(() => {
    // インストールボタンタップ時にセッションを作ると不要なダイアログが出るので、
    // そのダイアログが出ないようにワークアラウンドで画面表示にセッションを作る
    applicationDomain
      .installSessionToken()
      .then((result) => {
        if (result.isFailure()) {
          handleAlertWithGoBack(result.value.localeId());
          return;
        }

        setInstallSessionToken(result.value);
      })
      .catch((e) => {
        dispatch(setExceptionError(e));
      });

    applicationDomain
      .detail()
      .then((result) => {
        if (result.isFailure()) {
          handleAlertWithGoBack(result.value.localeId());
          return;
        }

        setApplication(result.value);
      })
      .catch((e) => {
        dispatch(setExceptionError(e));
      });

    setOtherApplications([]);
    callOtherVersion(undefined);
  }, [ownerName, packageName, revision]);

  const callOtherVersion = useCallback(
    async (maxRevision: number | undefined): Promise<number | undefined> => {
      return await applicationDomain
        .applications(maxRevision)
        .then((result) => {
          if (result.isFailure()) {
            handleAlertWithGoBack(result.value.localeId());
            return undefined;
          }

          const [availableApplications, nextMaxRevision] = result.value;
          setOtherApplications((state) => {
            return state.concat(availableApplications);
          });
          setNextMaxRevision(nextMaxRevision);
          return nextMaxRevision;
        })
        .catch((e) => {
          dispatch(setExceptionError(e));
          return undefined;
        });
    },
    [dispatch]
  );

  const handleCloseDialogAndGoBack = useCallback(() => {
    dispatch(closeDialog());
    history.goBack();
  }, [history, dispatch]);

  const handleClickSearch = useCallback((): void => {
    const searchPath = userName
      ? getRequestPath(ROUTER_PATH.userAppSearch, {
          userName,
          packageName,
          revision,
        })
      : getRequestPath(ROUTER_PATH.groupAppSearch, {
          groupName,
          packageName,
          revision,
        });

    history.push(searchPath);
  }, [history, packageName, revision, userName, groupName]);

  const handleAlertWithGoBack = useCallback(
    (id: string, values?: Record<string, string>) => {
      dispatch(
        showDialog({
          content: intl.formatMessage({ id }, values),
          defaultAction: {
            label: intl.formatMessage({ id: localeIds.dialog.ok }),
            callback: handleCloseDialogAndGoBack,
          },
        })
      );
    },
    [dispatch]
  );

  const handleShowOtherVersion = useCallback(async (): Promise<void> => {
    await callOtherVersion(nextMaxRevision);
  }, [callOtherVersion, nextMaxRevision]);

  const binaryInstall = useCallback(
    async (overwrite: boolean, application: Application): Promise<void> => {
      applicationDomain
        .install(installSessionToken as string, overwrite, application)
        .then((result) => {
          if (result.isFailure()) {
            dispatch(setErrorMessageLocaleId(result.value.localeId()));
            return;
          }

          dispatch(
            showDialog({
              content: intl.formatMessage({
                id: localeIds.alert.startBinaryInstall,
              }),
            })
          );
          if (overwrite || device?.detail?.mustOverwriteInstall) {
            setStartOverwriteInstall(true);
          } else {
            setStartInstall(true);
          }
        })
        .catch((e) => {
          dispatch(setExceptionError(e));
        });
    },
    [
      dispatch,
      installSessionToken,
      device,
      setStartOverwriteInstall,
      setStartInstall,
    ]
  );

  const handleCloseDialog = useCallback(() => {
    dispatch(closeDialog());
  }, [dispatch]);

  const reInstallProfile = useCallback(() => {
    dispatch(
      showDialog({
        content: intl.formatMessage({
          id: localeIds.configurationProfileInfo.confirm.reInstall,
        }),
        defaultAction: {
          label: intl.formatMessage({ id: localeIds.dialog.ok }),
          callback: async () => {
            dispatch(closeDialog());
            await reInstallConfigurationProfile().catch((e) => {
              dispatch(setExceptionError(e));
            });
          },
        },
        cancelAction: {
          label: intl.formatMessage({ id: localeIds.dialog.cancel }),
          callback: handleCloseDialog,
        },
      })
    );
  }, [dispatch]);

  const handleInstall = useCallback(() => {
    binaryInstall(false, application as Application);
  }, [binaryInstall, application]);

  const handleOverwriteInstall = useCallback(() => {
    binaryInstall(true, application as Application);
  }, [binaryInstall, application]);

  const handleUnsupportedOverwriteInstall = useCallback(() => {
    if (device?.detail) {
      handleAlertWithGoBack(localeIds.appInstall.unsupportedOverwriteInstall, {
        deviceOsVersion: device.detail.osVersion,
      });
    }
  }, [device]);

  const handleReInstallProfile = useCallback(() => {
    reInstallProfile();
  }, [reInstallProfile]);

  return device?.detail && application && otherApplications ? (
    <DefaultTemplate
      headerText={intl.formatMessage({
        id: localeIds.appDetailPageLayout.title,
      })}
      onBack={(): void => {
        history.push(ROUTER_PATH.apps);
      }}
    >
      <AppDetailPageLayout
        deviceDetail={device.detail}
        application={application}
        otherApplications={otherApplications}
        existOtherVersion={nextMaxRevision !== undefined}
        startInstall={startInstall}
        startOverwriteInstall={startOverwriteInstall}
        onInstall={handleInstall}
        onOverwriteInstall={handleOverwriteInstall}
        onUnsupportedOverwriteInstall={handleUnsupportedOverwriteInstall}
        onShowOtherVersion={handleShowOtherVersion}
        installReady={!!installSessionToken}
        onReInstallProfile={handleReInstallProfile}
        onClickSearch={handleClickSearch}
      />
    </DefaultTemplate>
  ) : (
    <DefaultTemplate headerText="">
      <Splash />
    </DefaultTemplate>
  );
};
